/**
 * Identifies a plugin-supplied icon as being an SVG path instead of an icon class
 */
export const SVG_PATH = 'svgpath:';
/**
 * Determines if an icon is SVG
 *
 * @param icon - an icon string that will either be an icon class or an SVG path definition (e.g. "fa fa-wrench" or
 * "svgpath:M 17.0181 0 ...")
 */
export function isSvgIcon(icon: string): boolean {
  return icon.startsWith(SVG_PATH);
}

/**
 * Retrieves the SVG path from an SVG icon
 *
 * @param icon - an SVG icon string including path definition (e.g. "svgpath:M 17.0181 0 ...")
 * @returns the SVG icon path or an empty string if the supplied icon is not SVG
 */
export function getSvgIconPath(icon: string): string {
  return isSvgIcon(icon) ? icon.substring(SVG_PATH.length) : '';
}

export interface SvgIconProps {
  icon: string;
  iconFill: string;
  extraClassNames?: string;
  height?: string;
  width?: string;
  testId?: string;
}

/**
 * Renders an icon that has an SVG icon path (see {@link isSvgIcon() })
 *
 * @param icon - the SVG icon
 * @param extraClassNames - extra class names to apply
 * @param [viewBox='0 0 19 19'] - optional SVG view box
 * @param testId TestID to add to component
 */
export const SvgIcon: React.FunctionComponent<SvgIconProps> = ({
  icon,
  iconFill,
  extraClassNames,
  height = '19',
  width = '19',
  testId,
}) => {
  const viewBox = `0 0 ${height} ${width}`;
  return (
    <svg
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
      className={extraClassNames}
      height={height}
      width={width}
      data-testid={testId}>
      <path d={getSvgIconPath(icon)} fill={iconFill} />
    </svg>
  );
};
